import { useState, useEffect, useCallback } from 'react';
import { getHours, setHours, updateDay } from '../api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultHours = DAYS.map(day => ({
  day_of_week: day, open_time: '09:00', close_time: '22:00', is_closed: false
}));


const s = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f3', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  loading: { padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '13px' },
  topNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontSize: '15px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.3px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  navLink: { fontSize: '13px', color: '#666', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  subNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', display: 'flex' },
  subNavBtn: { padding: '12px 16px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '13px', color: '#aaa', cursor: 'pointer', fontWeight: '500' },
  subNavBtnActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },
  content: { maxWidth: '700px', margin: '0 auto', padding: '32px 24px' },
  card: { backgroundColor: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '8px', padding: '24px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  successMsg: { backgroundColor: '#f0faf4', color: '#2e7d32', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  hint: { fontSize: '12px', color: '#aaa', marginBottom: '20px' },
  row: { display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: '0.5px solid #f5f5f5', flexWrap: 'wrap' },
  dayName: { width: '100px', fontSize: '13px', fontWeight: '500', color: '#1a1a1a', flexShrink: 0 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666', cursor: 'pointer', width: '70px', flexShrink: 0 },
  timeRow: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 },
  timeInput: { padding: '6px 10px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', color: '#1a1a1a' },
  timeSep: { fontSize: '12px', color: '#aaa' },
  saveDay: { padding: '5px 12px', backgroundColor: '#fff', color: '#444', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  btnPrimary: { padding: '9px 18px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnDisabled: { padding: '9px 18px', backgroundColor: '#ccc', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500' },
};

function OpeningHours({ onBack, onNavigate }) {
  const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
  const [hours, setHoursState] = useState(defaultHours);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);

  const fetchHours = useCallback(async () => {
    try {
      const res = await getHours();
      if (res.data.length === 0) {
        setIsFirstTime(true);
      } else {
        const merged = DAYS.map(day => {
          const found = res.data.find(h => h.day_of_week === day);
          return found || { day_of_week: day, open_time: '09:00', close_time: '22:00', is_closed: false };
        });
        setHoursState(merged);
      }
    } catch (err) {
      setError('Failed to load opening hours');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHours(); }, [fetchHours]);

  const handleChange = (index, field, value) => {
    const updated = [...hours];
    updated[index] = { ...updated[index], [field]: value };
    setHoursState(updated);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await setHours({ hours });
      setSuccess('Opening hours saved.');
      setIsFirstTime(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save opening hours');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDay = async (day, index) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const entry = hours[index];
      await updateDay(day, { open_time: entry.open_time, close_time: entry.close_time, is_closed: entry.is_closed });
      setSuccess(day + ' updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update ' + day);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  const nav = (tab) => { if (onNavigate) onNavigate(tab); else onBack(); };

  return (
    <div style={s.page}>
      <div style={s.topNav}>
        <span style={s.navBrand}>{restaurant.name || 'Digital Menu'}</span>
        <div style={s.navRight}>
          <a href={'/menu/' + restaurant.slug} target="_blank" rel="noreferrer" style={s.navLink}>View menu</a>
          <button style={s.navLink} onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('restaurant'); window.location.href = '/'; }}>Sign out</button>
        </div>
      </div>
            <div style={s.subNav}>
        <button style={{...s.subNavBtn, ...('menus' === 'hours' ? s.subNavBtnActive : {})}} onClick={() => nav('menus')}>Menus</button>
        <button style={{...s.subNavBtn, ...('hours' === 'hours' ? s.subNavBtnActive : {})}} onClick={() => nav('hours')}>Hours</button>
        <button style={{...s.subNavBtn, ...('profile' === 'hours' ? s.subNavBtnActive : {})}} onClick={() => nav('profile')}>Profile</button>
        <button style={{...s.subNavBtn, ...('staff' === 'hours' ? s.subNavBtnActive : {})}} onClick={() => nav('staff')}>Staff</button>
        <button style={{...s.subNavBtn, ...('settings' === 'hours' ? s.subNavBtnActive : {})}} onClick={() => nav('settings')}>Settings</button>
      </div>

      <div style={s.content}>
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Opening Hours</h2>
            <button style={saving ? s.btnDisabled : s.btnPrimary} onClick={handleSaveAll} disabled={saving}>
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>

          {error && <p style={s.error}>{error}</p>}
          {success && <p style={s.successMsg}>{success}</p>}

          <p style={s.hint}>
            {isFirstTime ? 'Set your opening hours and click Save All.' : 'Update individual days or save all at once.'}
          </p>

          {hours.map((entry, index) => (
            <div key={entry.day_of_week} style={s.row}>
              <span style={s.dayName}>{entry.day_of_week}</span>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={entry.is_closed} onChange={(e) => handleChange(index, 'is_closed', e.target.checked)} />
                Closed
              </label>
              <div style={s.timeRow}>
                <input type="time" value={entry.open_time || '09:00'} disabled={entry.is_closed} onChange={(e) => handleChange(index, 'open_time', e.target.value)} style={{ ...s.timeInput, opacity: entry.is_closed ? 0.35 : 1 }} />
                <span style={s.timeSep}>to</span>
                <input type="time" value={entry.close_time || '22:00'} disabled={entry.is_closed} onChange={(e) => handleChange(index, 'close_time', e.target.value)} style={{ ...s.timeInput, opacity: entry.is_closed ? 0.35 : 1 }} />
              </div>
              {!isFirstTime && (
                <button style={s.saveDay} onClick={() => handleSaveDay(entry.day_of_week, index)} disabled={saving}>Save</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default OpeningHours;
